/**
 * Spec: interlude `plans/whatsapp-conversational-components-api-contract.md`
 * -- `prompts`: max 4 entries, each 80 characters or fewer, no emoji, no
 * blank entries.
 */
export const ICE_BREAKER_MAX_COUNT = 4;
export const ICE_BREAKER_MAX_LENGTH = 80;

/**
 * Detects whether `value` contains at least one character that actually
 * renders as an emoji.
 *
 * `\p{Extended_Pictographic}` alone is too broad: it also matches
 * characters like the copyright sign and the registered-trademark sign,
 * which Unicode classifies as pictographic but which render as plain text
 * by default (`Emoji_Presentation=No`). Rejecting those would block
 * legitimate copy such as a trademarked shop name.
 *
 * This instead checks, in order:
 * - `\p{Emoji_Presentation}` -- codepoints that render as emoji by
 *   default. Covers the large majority of emoji, including ZWJ sequences:
 *   each component of a family/couple sequence is itself
 *   Emoji_Presentation=Yes, so the sequence is caught without
 *   special-casing the joiner.
 * - `\p{Extended_Pictographic}` immediately followed by U+FE0F (variation
 *   selector-16) -- text-default pictographs (e.g. a plain airplane glyph,
 *   same category as the copyright/registered signs above) explicitly
 *   forced into emoji rendering by a trailing variation selector.
 * - A pair of regional indicators -- flag sequences (e.g. a country flag).
 * - A keycap sequence -- digit/#/* + optional U+FE0F + U+20E3 (combining
 *   enclosing keycap), e.g. a "1" keycap emoji.
 *
 * The API contract (see the interlude spec doc) doesn't define emoji
 * precisely, and the backend validates independently against Meta's own
 * rule -- this is a best-effort match for the common cases on the client,
 * not a guarantee of matching the backend's rule exactly. Flagged to the
 * team as a contract ambiguity.
 *
 * The variation-selector/keycap codepoints below are spelled out as
 * `\uXXXX` escapes rather than pasted as literal glyphs -- an invisible
 * character sitting directly in source is impossible to review (a diff
 * can't show what it is).
 */
const EMOJI_PATTERN = new RegExp(
  [
    "\\p{Emoji_Presentation}",
    "\\p{Extended_Pictographic}\uFE0F",
    "\\p{Regional_Indicator}{2}",
    "[0-9#*]\uFE0F?\u20E3",
  ].join("|"),
  "u",
);

export function hasEmoji(value: string): boolean {
  return EMOJI_PATTERN.test(value);
}

export type IceBreakerFieldError = "empty" | "tooLong" | "emoji";

/**
 * Validates a single ice breaker's text against the contract's per-item
 * rules. Returns the first violated rule, or `null` when the value is valid.
 */
export function getIceBreakerFieldError(
  value: string,
): IceBreakerFieldError | null {
  if (value.trim().length === 0) return "empty";
  if (value.length > ICE_BREAKER_MAX_LENGTH) return "tooLong";
  if (hasEmoji(value)) return "emoji";
  return null;
}

/**
 * Whether every ice breaker is valid and the list is within the contract's
 * count limit. Used to gate the Save button. An empty list is valid -- it
 * represents the user clearing all ice breakers.
 */
export function isIceBreakersListValid(values: ReadonlyArray<string>): boolean {
  return (
    values.length <= ICE_BREAKER_MAX_COUNT &&
    values.every((value) => getIceBreakerFieldError(value) === null)
  );
}
